import * as React from 'react';
import classnames from 'classnames';
import ReactDOM from 'react-dom';
import { connectCurrentRefinements, connectStats } from 'react-instantsearch-core';
import { Util, Button } from '@grandvisionhq/common';
import { Heading } from '@grandvisionhq/elements';
import { useIntl } from '@grandvisionhq/state';
import { FacetId } from '../../../types.js';
import { useItemsToClear } from '../../hooks/use-items-to-clear.js';
import { ClearFacetFilter } from '../ClearFacetFilter/ClearFacetFilter.js';
import ConnectedFacetContainerMobile from '../FacetContainerMobile/ConnectedFacetContainerMobile.js';
import ConnectedFacetInputPriceRangeMobile from '../FacetInputPriceRange/ConnectedFacetInputPriceRangeMobile.js';

const FacetsMobile = ({ facets , items: items1 , refine , nbHits  })=>{
    const { getLabel  } = useIntl();
    const [hasMounted, setMounted] = React.useState(false);
    const [activeFacet, setActiveFacet] = React.useState();
    const [isVisible, setVisible] = React.useState(false);
    const [lastSavedScrollHeight, setLastSavedScrollHeight] = React.useState(0);
    const [isSticky1, setSticky] = React.useState(false);
    const [touched, setTouched] = React.useState(false);
    const filterButton = React.useRef(null);
    React.useEffect(()=>setMounted(true)
    , []);
    const handleToggle = React.useCallback((attribute)=>{
        window.scrollTo(0, 0);
        setActiveFacet(activeFacet === attribute ? undefined : attribute);
    }, [
        activeFacet
    ]);
    const onShowFilters = React.useCallback(()=>{
        setTouched(false);
        setLastSavedScrollHeight(window.scrollY);
        setVisible(!isVisible) // set isVisible to true
        ;
        window.scrollTo(0, 0);
    }, [
        isVisible
    ]);
    const onHideFilters = React.useCallback(()=>{
        setVisible(!isVisible) // set isVisible to false
        ;
    }, [
        isVisible
    ]);
    React.useEffect(()=>{
        if (!isVisible) {
            var ref;
            const filterButtonPosition = ((ref = filterButton.current) === null || ref === void 0 ? void 0 : ref.getBoundingClientRect().top) || 160;
            touched ? window.scrollTo(0, filterButtonPosition) : window.scrollTo(0, lastSavedScrollHeight);
        }
    }, [
        isVisible,
        lastSavedScrollHeight,
        touched
    ]);
    const itemsToClear = useItemsToClear(items1);
    const { isHidden: isHeaderHidden  } = Util.useHeaderOnScroll(true);
    const activeFilterCount = itemsToClear.filter((item, index, items)=>items.findIndex(({ id  })=>id === item.id
        ) === index
    ).reduce((prev, curr)=>{
        const itemCount = curr.items && curr.items.length || 1;
        return prev + itemCount;
    }, 0);
    const handleClear = React.useCallback(()=>{
        setTouched(true);
        refine(itemsToClear) // typecasting due to a bug in algolia types
        ;
    }, [
        itemsToClear,
        refine,
        setTouched
    ]);
    const handleChange = React.useCallback(()=>setTouched(true)
    , [
        setTouched
    ]);
    const handleScroll = React.useCallback(()=>{
        if (filterButton.current) {
            var ref;
            // Scrolling up reveals the Header, which is 62px in height
            const benchmark = isHeaderHidden ? 0 : 62;
            const isSticky = ((ref = filterButton.current) === null || ref === void 0 ? void 0 : ref.getBoundingClientRect().top) < benchmark;
            setSticky(isSticky);
        }
    }, [
        isHeaderHidden,
        setSticky,
        filterButton
    ]);
    // Adds scroll listener that makes the filterbutton sticky
    React.useEffect(()=>{
        window.addEventListener('scroll', handleScroll);
        return ()=>window.removeEventListener('scroll', handleScroll)
        ;
    }, [
        handleScroll
    ]);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, !isVisible && /*#__PURE__*/ React.createElement("div", {
        className: "facets-mobile-trigger",
        ref: filterButton
    }, /*#__PURE__*/ React.createElement("div", {
        className: classnames({
            'facets-mobile-trigger__sticky': isSticky1,
            'facets-mobile-trigger__sticky--with-header': !isHeaderHidden && isSticky1
        })
    }, /*#__PURE__*/ React.createElement(Button, {
        className: "facets-mobile-trigger__button",
        caption: `${getLabel('search.filter')}
    ${activeFilterCount > 0 ? `(${activeFilterCount})` : ''}`,
        icon: "filter",
        onClick: onShowFilters
    }))), hasMounted && /*#__PURE__*/ ReactDOM.createPortal(renderFacets(), document.body), isVisible && /*#__PURE__*/ React.createElement(HideApp, null));
    function renderFacets() {
        const facetsClass = classnames('facets-mobile', {
            'facets-mobile--hidden': !isVisible
        });
        const facetHeaderClass = classnames('facets-mobile__header', {
            'facets-mobile__header--hidden': !!activeFacet
        });
        const facetContainerClass = classnames('facets-mobile__container', {
            'facets-mobile__container--layered': !!activeFacet
        });
        const footerButtonClass = classnames('facets-mobile__footer-button', {
            'facets-mobile__footer-button': !!activeFacet
        });
        return /*#__PURE__*/ React.createElement("div", {
            className: facetsClass
        }, /*#__PURE__*/ React.createElement("div", {
            className: facetHeaderClass
        }, /*#__PURE__*/ React.createElement(Heading, {
            variant: "small",
            className: "facets-mobile__title"
        }, getLabel('products.filters'), " ", !!items1.length && `(${activeFilterCount})`), /*#__PURE__*/ React.createElement(ClearFacetFilter, {
            label: getLabel('search.filters.clearAll'),
            onClear: handleClear,
            isDisabled: !itemsToClear.length
        })), /*#__PURE__*/ React.createElement("div", {
            className: facetContainerClass
        }, facets.map(renderFacet)), /*#__PURE__*/ React.createElement("div", {
            className: "facets-mobile__footer"
        }, activeFacet ? /*#__PURE__*/ React.createElement(Button, {
            className: footerButtonClass,
            caption: getLabel('search.filters.save'),
            variant: "outline",
            onClick: ()=>handleToggle(undefined)
        }) : /*#__PURE__*/ React.createElement(Button, {
            className: footerButtonClass,
            caption: activeFilterCount > 0 ? `${getLabel('search.filters.showResults')} ${nbHits}` : getLabel('search.filters.close'),
            variant: activeFilterCount > 0 ? 'solid' : 'outline',
            onClick: onHideFilters
        })));
    }
    function renderFacet(facet) {
        switch(facet.id){
            case FacetId.price:
                return /*#__PURE__*/ React.createElement(ConnectedFacetInputPriceRangeMobile, {
                    id: facet.id,
                    key: facet.attribute,
                    attribute: facet.attribute,
                    label: getLabel(facet.labelKey || facet.id),
                    isActive: activeFacet === facet.attribute,
                    expandedState: !!activeFacet,
                    onChange: handleChange
                });
            default:
                var _label;
                return /*#__PURE__*/ React.createElement(ConnectedFacetContainerMobile, {
                    key: facet.attribute,
                    facet: facet,
                    searchable: facet.searchable,
                    attribute: facet.attribute,
                    isActive: facet.attribute === activeFacet,
                    expandedState: !!activeFacet,
                    limit: 100,
                    label: (_label = facet.label) !== null && _label !== void 0 ? _label : getLabel(facet.labelKey || facet.id),
                    onChange: handleChange
                });
        }
    }
};
var FacetsMobile$1 = connectCurrentRefinements(connectStats(FacetsMobile));
const HideApp = ()=>{
    React.useEffect(()=>{
        const container = document.getElementById('app-container');
        if (container) {
            container.style.display = 'none';
        }
        return function cleanUp() {
            if (container) {
                container.style.display = '';
            }
        };
    });
    return null;
};

export { FacetsMobile, FacetsMobile$1 as default };
//# sourceMappingURL=FacetsMobile.js.map
